
(() => {
  if (window.ibkulOnlineExclusiveBadgeInitialized) return;
  window.ibkulOnlineExclusiveBadgeInitialized = true;

  const normalize = (value) =>
    String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");

  const parseJSON = (value, fallback = null) => {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  function getColors(gallery) {
    const raw = gallery.dataset.ibkulOnlineExclusiveColors;

    if (!raw || !raw.trim() || raw.trim() === "null") return [];

    const parsed = parseJSON(raw, raw);
    let values = [];

    if (Array.isArray(parsed)) {
      values = parsed;
    } else if (typeof parsed === "string") {
      const nested = parseJSON(parsed, null);
      values = Array.isArray(nested)
        ? nested
        : parsed.split(/[,;\n]/);
    }

    return values.map((value) => {
      if (typeof value === "string") return normalize(value);
      if (value && typeof value === "object") {
        return normalize(value.value ?? value.name ?? value.label ?? "");
      }
      return "";
    }).filter(Boolean);
  }

  function getProductSection(gallery) {
    return gallery.closest(".shopify-section") || document;
  }

  function getForms(gallery) {
    const section = getProductSection(gallery);
    return [...section.querySelectorAll('form[action*="/cart/add"]')];
  }

  function getSelectedOptions(form, options) {
    if (!form) return null;

    const selected = [];

    for (let i = 0; i < options.length; i++) {
      const number = i + 1;
      const optionName = String(options[i]);

      // Standard Shopify option controls.
      let control =
        form.querySelector(`select[name="options[${CSS.escape(optionName)}]"]`) ||
        form.querySelector(`select[name="option${number}"]`);

      if (control) {
        selected.push(control.value);
        continue;
      }

      const radio =
        form.querySelector(
          `input[type="radio"][name="options[${CSS.escape(optionName)}]"]:checked`
        ) ||
        form.querySelector(
          `input[type="radio"][name="option${number}"]:checked`
        );

      if (radio) {
        selected.push(radio.value);
        continue;
      }

      // Some themes render selected option values on buttons.
      const selectedButton = form.querySelector(
        `[data-option-name="${CSS.escape(optionName)}"] [aria-checked="true"], ` +
        `[data-option-name="${CSS.escape(optionName)}"] [aria-pressed="true"], ` +
        `[data-option-name="${CSS.escape(optionName)}"] [data-selected="true"]`
      );

      if (selectedButton) {
        const value =
          selectedButton.dataset.value ||
          selectedButton.getAttribute("data-option-value") ||
          selectedButton.textContent;

        selected.push(String(value).trim());
        continue;
      }

      return null;
    }

    return selected;
  }

  function getVariant(gallery, variants, options) {
    const forms = getForms(gallery);

    // Prefer the URL variant when the theme updates it on selection.
    const urlId = new URLSearchParams(location.search).get("variant");

    if (urlId) {
      const urlVariant = variants.find(
        (variant) => String(variant.id) === String(urlId)
      );

      if (urlVariant) return urlVariant;
    }

    // Match the currently selected options against actual variants.
    for (const form of forms) {
      const selectedOptions = getSelectedOptions(form, options);

      if (selectedOptions) {
        const matched = variants.find((variant) =>
          variant.options?.every(
            (value, index) =>
              normalize(value) === normalize(selectedOptions[index])
          )
        );

        if (matched) return matched;
      }
    }

    // Fallback to the current product form's variant ID.
    for (const form of forms) {
      const id = form.querySelector('[name="id"]')?.value;

      const matched = variants.find(
        (variant) => String(variant.id) === String(id)
      );

      if (matched) return matched;
    }

    return null;
  }

  function updateGallery(gallery) {
    const badges = gallery.querySelectorAll(
      "[data-ibkul-online-exclusive-badge]"
    );

    if (!badges.length) return;

    const variants = parseJSON(gallery.dataset.ibkulVariants, []);
    const options = parseJSON(gallery.dataset.ibkulOptions, []);
    const colors = getColors(gallery);

    const colorIndex = options.findIndex((option) =>
      /^(color|colour)$/i.test(String(option).trim())
    );

    let show = false;

    if (variants.length && colors.length && colorIndex >= 0) {
      const variant = getVariant(gallery, variants, options);
      const selectedColor = variant
        ? normalize(variant.options?.[colorIndex])
        : "";

      show = Boolean(selectedColor && colors.includes(selectedColor));
    }

    badges.forEach((badge) => {
      badge.hidden = !show;
    });
  }

  function updateAll() {
    document.querySelectorAll("media-gallery").forEach(updateGallery);
  }

  let scheduled = false;

  function scheduleUpdate() {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      updateAll();
    });
  }

  // Catch color swatch clicks, option changes and theme events.
  document.addEventListener("click", () => {
    scheduleUpdate();
    setTimeout(scheduleUpdate, 50);
    setTimeout(scheduleUpdate, 150);
    setTimeout(scheduleUpdate, 350);
  }, true);

  document.addEventListener("change", scheduleUpdate);
  document.addEventListener("input", scheduleUpdate);

  [
    "variant:change",
    "variant:changed",
    "product:variant-change",
    "product:variant:change"
  ].forEach((eventName) => {
    document.addEventListener(eventName, scheduleUpdate);
  });

  // Detect option markup changes made by theme or swatch app scripts.
  const observer = new MutationObserver(scheduleUpdate);

  function init() {
    updateAll();

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        "value",
        "checked",
        "selected",
        "aria-checked",
        "aria-pressed",
        "data-value",
        "data-selected"
      ]
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", scheduleUpdate);
})();
